import React, { useState, useEffect } from "react";
import { Table, Button, Form, Spinner } from "react-bootstrap";

// --- CHILD COMPONENT: Handles individual Row logic ---
const WordRow = ({ item, index, onUpdateSuccess }) => {
  const [editedEnglish, setEditedEnglish] = useState(item.EnglishWords || "");
  const [isChanged, setIsChanged] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync state if parent data changes
  useEffect(() => {
    setEditedEnglish(item.EnglishWords || "");
    setIsChanged(false);
  }, [item.EnglishWords]);

  const handleInputChange = (e) => {
    setEditedEnglish(e.target.value);
    setIsChanged(true);
  };

  const handleUpdate = async () => {
    if (!editedEnglish.trim()) {
        alert("Please enter a valid word");
        return;
    }
    
    setIsUpdating(true);

    // Using the payload structure from your provided code
    const payload = {
      data: {
        EnglishWords: editedEnglish,
        AutoEnglishWords: editedEnglish // Updated: sending User Input for both as per your previous request
      },
      where: `MarathiWordId = ${item.MarathiWordId}`
    };

    try {
      const response = await fetch("https://smartsearch.anuplights.com/api/Updatemarathiwords", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.affectedRows > 0 || result.changedRows > 0) {
        onUpdateSuccess(item.MarathiWordId, editedEnglish);
        setIsChanged(false);
        // Optional: Show a small success indicator instead of alert
      } else {
        alert("No changes saved.");
      }
    } catch (error) {
      console.error("Update failed", error);
      alert("Update Failed");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <tr>
      <td style={{ textAlign: "center" }}>{index + 1}</td>
      <td style={{ fontWeight: 600, color: "#555" }}>{item.MarathiWords}</td>
      <td>{item.EnglishWords}</td>
      <td style={{ color: "#777", fontStyle: "italic" }}>{item.AutoEnglishWords}</td>
      <td>
        <Form.Control
          type="text"
          value={editedEnglish}
          onChange={handleInputChange}
          placeholder="Enter correct word..."
          style={{ borderColor: isChanged ? "#ffc107" : "#ced4da" }} // Visual cue
        />
      </td>
      <td>
        <Button
          variant={isChanged ? "success" : "secondary"} // Green if changed, Gray if not
          size="sm"
          onClick={handleUpdate}
          disabled={isUpdating || !isChanged}
        >
          {isUpdating ? <Spinner size="sm" animation="border" /> : "Update"}
        </Button>
      </td>
    </tr>
  );
};

// --- MAIN PARENT COMPONENT (Sidebar Version) ---
function WordManager({ onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // API Call to get all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://smartsearch.anuplights.com/api/GetAllmarathiwords", {
        method: "POST",
      });
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update local state when a child row updates successfully
  const handleLocalUpdate = (id, newEnglishWord) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.MarathiWordId === id
          ? { ...item, EnglishWords: newEnglishWord, AutoEnglishWords: newEnglishWord }
          : item
      )
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 240,
        right: 0,
        bottom: 0,
        background: "#f6f7fb",
        padding: 20,
        overflowY: "auto",
        zIndex: 9999,
      }}
    >
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <h3>Marathi Word Manager</h3>
        <Button variant="secondary" onClick={onClose}>
          ← Back
        </Button>
      </div>

      {/* TABLE */}
      <div style={{ background: "#fff", borderRadius: 8, padding: 10 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Table hover responsive bordered>
            <thead style={{ background: "#f1f3f6" }}>
              <tr>
                <th style={{ width: 60, textAlign: "center" }}>Sr No</th>
                <th>Marathi Name</th>
                <th>English Name</th>
                <th>Auto Name</th>
                <th style={{ width: 250 }}>Correct Eng Word (Input)</th>
                <th style={{ width: 100 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <WordRow
                  key={item.MarathiWordId}
                  item={item}
                  index={index}
                  onUpdateSuccess={handleLocalUpdate}
                />
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
}

export default WordManager;